/**
 * The board's one audio engine, and the wait for the recordings a score is scored for.
 *
 * Two panels want this and want it identically -- the editor, and each half of the
 * side-by-side audition -- so it is here rather than written out twice: a browser will only
 * make an audio context after a gesture, the context is wrapped in a {@link SampledEngine}
 * so that parts are heard on recordings of their own instruments, and those recordings have
 * to be fetched and decoded before the first note is due.
 *
 * It is two hooks and not one because the second needs what the first is passed to: the
 * schedule that names the instruments is made by `usePlayback`, which is given the engine.
 * So a panel wakes the engine, hands it to `usePlayback`, and hands the schedule back here.
 *
 * There is one engine for the whole board rather than one per panel, which is why it is held
 * in this module and not in a component. The arrange board mounts two of these panels at
 * once to be compared by ear, and a context each would mean the same recordings fetched and
 * decoded twice over -- tens of megabytes of audio held twice so that two panels that are
 * never allowed to sound at the same time can each own one.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { Schedule } from "@app/score-editor";
import { SampledEngine } from "./sampler";
import type { Sound } from "./sampler-load";

export interface WokenEngine {
  /** The engine, or `null` until a gesture has been made and one could be built. */
  readonly engine: SampledEngine | null;
  /**
   * Build the engine if there is none, wake it if it is asleep, and hand it over.
   *
   * It answers out of the module rather than out of state because two gestures can land
   * before React has rendered the first -- pressing play and attaching a keyboard, or the
   * two halves of the audition -- and reading the live value is what makes the second find
   * the engine the first made instead of making a second context.
   */
  wake(): SampledEngine;
}

/** The board's engine, once a gesture has allowed one to exist. */
let board: SampledEngine | null = null;

/** Every mounted hook, so that a panel which did not make the engine still learns of it. */
const watching = new Set<() => void>();

function subscribe(tell: () => void): () => void {
  watching.add(tell);
  return () => {
    watching.delete(tell);
  };
}

function current(): SampledEngine | null {
  return board;
}

export function useSampledEngine(): WokenEngine {
  const engine = useSyncExternalStore(subscribe, current, current);

  const wake = useCallback((): SampledEngine => {
    if (board === null) {
      board = new SampledEngine(new AudioContext());
      for (const tell of watching) tell();
    }
    void board.resume();
    return board;
  }, []);

  return { engine, wake };
}

/**
 * Every sound heard in a schedule: each instrument as its General MIDI program, and a
 * string once more where it is plucked, which is recorded apart from the same string bowed.
 *
 * Read off the played notes rather than off the parts, because that is where the answer
 * already is: a part that takes up another instrument half way through has put both programs
 * on its notes, and re-deriving it from the score would be a second copy of a rule the
 * schedule has already applied.
 */
export function soundsOf(schedule: Schedule): readonly Sound[] {
  const sounds = new Map<string, Sound>();
  for (const { program, plucked } of schedule.notes) {
    sounds.set(`${program}:${plucked}`, { program, plucked });
  }
  return [...sounds.values()];
}

/**
 * Whether this schedule can be played on its own instruments yet.
 *
 * `false` means a fetch is in flight, and a panel that has been asked to play should hold
 * the press rather than refuse it: the wait is once and short, and starting without the
 * samples would sound the opening bars on oscillators and the rest on an orchestra.
 *
 * It runs again on every edit, because an edit can bring in an instrument that was not
 * heard before. Asking the engine first is what keeps that cheap: an edit that changed no
 * instrument is answered without a promise, and the transport does not blink.
 */
export function useSamplesFor(engine: SampledEngine | null, schedule: Schedule): boolean {
  const [ready, setReady] = useState(false);
  const sounds = useMemo(() => soundsOf(schedule), [schedule]);

  useEffect(() => {
    if (engine === null) return;
    if (engine.ready(sounds)) {
      setReady(true);
      return;
    }
    let wanted = true;
    setReady(false);
    void engine.load(sounds).finally(() => {
      if (wanted) setReady(true);
    });
    return () => {
      wanted = false;
    };
  }, [engine, sounds]);

  return ready;
}
